// BrandSaleReport.jsx
// ✅ Brand + Total Sales (Amount) only
// ✅ FromDate / ToDate filters
// ✅ Branch filter
// ✅ Brand wise filter ✅ (GET_BRANDS uses id)
// ✅ Sorting + totals + CSV export
// ✅ Handles weird API row where "Item Name": {} by showing "—"

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

// ---------- HELPERS ----------
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd

// ✅ ONLY TWO COLUMNS
const COLUMNS = [
  { key: 'Brand', label: 'Brand' },
  { key: 'TotalSales', label: 'Sales Amount', align: 'right' },
];

const BrandSaleReport = ({ Currency }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // ✅ Brand filter state
  const [selectedBrand, setSelectedBrand] = useState(null);

  // ✅ Brands list from GET_BRANDS
  const [brands, setBrands] = useState([]);

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0,
  });

  // ---------- FETCH BRANCHES ----------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        })),
      );
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ---------- FETCH BRANDS (GET_BRANDS) ----------
  const fetchBrands = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANDS');
      const list = res?.data?.result || [];

      // your response: { id, name, code, ... }
      const normalized = list
        .map((b) => ({
          id: b?.id,
          name: (b?.name || '').trim(),
          isActive: b?.isActive,
        }))
        .filter((x) => x.id != null && x.name);

      setBrands(normalized);
    } catch (e) {
      console.error('Failed to fetch brands', e);
      setBrands([]);
    }
  };

  // ---------- FETCH REPORT ----------
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // ✅ This is the key for this report
      params.append('ReportType', 'Brand Sale Report');
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');
      params.append('PageSize', 200);
      params.append('PageNo', 1);

      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      // ✅ USE ID (not name)
      // If backend supports it, it will filter server-side
      if (selectedBrand?.value != null) params.append('BrandId', selectedBrand.value);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      // ✅ Normalize API keys
      const normalized = result.map((r) => {
        const rawBrand = r['Item Name'] ?? r.ItemName ?? r.itemName ?? r.Brand ?? r.BrandName ?? r.brandName ?? '';

        const brand = typeof rawBrand === 'string' ? rawBrand : rawBrand?.name || '';

        const totalSales = Number(r['Total Sales'] ?? r.TotalSales ?? r.totalSales ?? r.Amount ?? 0) || 0;

        // ✅ Keep BrandId if backend provides it (helps client filter if needed)
        const brandId = r.BrandId ?? r.brandId ?? r['Brand Id'] ?? r['BrandID'] ?? r.BrandID ?? null;

        return {
          Brand: (brand || '').trim() || '—',
          TotalSales: totalSales,
          BrandId: brandId, // hidden, not shown in table
        };
      });

      // ✅ Merge duplicate brand rows (same as before)
      const map = new Map();
      for (const row of normalized) {
        const key = row.Brand;
        if (!map.has(key)) map.set(key, { ...row });
        else map.get(key).TotalSales += row.TotalSales;
      }

      const grouped = Array.from(map.values());

      setRows(grouped);

      setPagination((p) => ({
        ...p,
        ...(res?.data?.paginationData || {}),
        totalRecords: grouped.length,
        currentPage: page,
        pageSize: size,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    fetchBranches();
    fetchBrands();
  }, []);

  // ---------- BRAND OPTIONS (from GET_BRANDS, value=id) ----------
  const brandOptions = useMemo(() => {
    return brands
      .slice()
      .filter((b) => b.isActive !== false) // optional: keep active (doesn't affect if all active)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((b) => ({
        value: b.id, // ✅ id
        label: b.name, // display name
      }));
  }, [brands]);

  // ---------- APPLY BRAND FILTER (client-side fallback) ----------
  // If backend already filtered by BrandId, this doesn't change anything.
  // If backend ignores BrandId, but report rows include BrandId, it will still work.
  const filteredRows = useMemo(() => {
    if (selectedBrand?.value == null) return rows;
    const id = String(selectedBrand.value);

    // Prefer filtering by BrandId if present; else fallback to label match
    const hasAnyBrandId = rows.some((r) => r.BrandId != null);

    if (hasAnyBrandId) {
      return rows.filter((r) => String(r.BrandId ?? '') === id);
    }

    return rows.filter((r) => r.Brand === selectedBrand.label);
  }, [rows, selectedBrand]);

  // ---------- SORT ----------
  const [sortState, setSortState] = useState({ key: 'TotalSales', dir: 'desc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      // numeric sort if both numeric
      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? +av - +bv : +bv - +av;
      }

      // string sort
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [filteredRows, sortState]);

  const totals = useMemo(
    () => ({
      amt: sortedRows.reduce((s, r) => s + (Number(r.TotalSales) || 0), 0),
    }),
    [sortedRows],
  );

  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  // ---------- CSV EXPORT ----------
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows
      .map((r) =>
        COLUMNS.map((c) => {
          const val = r[c.key];
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(','),
      )
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-by-brand-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="All Branches"
            isClearable
          />
        </div>

        {/* ✅ Brand filter (value=id, label=name) */}
        <div className="col-md-3">
          <label className="form-label">Select Brand</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBrand}
            onChange={(opt) => setSelectedBrand(opt)}
            options={brandOptions}
            placeholder="All Brands"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-6 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    <td>{r.Brand}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.TotalSales)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.amt)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(BrandSaleReport);
